const JET = "#0A0A0B";
const CREAM = "#F3F0E9";
const GOLD = "#C8A24C";

function wrapText(ctx, text, maxWidth, maxLines) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines) break;
    } else {
      line = test;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  else if (lines.length === maxLines) lines[maxLines - 1] = lines[maxLines - 1].replace(/\s*\S*$/, " …");
  return lines;
}

function drawBrainDots(ctx, cx, cy, rx, ry) {
  for (let i = 0; i < 900; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random());
    let x = Math.cos(a) * r;
    let y = Math.sin(a) * r * 0.82;
    const w = 0.06 * Math.sin(x * 9) * Math.sin(y * 11);
    x += x * w; y += y * w;
    if (Math.abs(x) < 0.02 && y < 0.1) continue;
    x += Math.sign(x) * 0.02;
    ctx.fillStyle = Math.random() < 0.16 ? GOLD : CREAM;
    ctx.globalAlpha = 0.25 + Math.random() * 0.55;
    const s = 1.2 + Math.random() * 1.8;
    ctx.fillRect(cx + x * rx, cy + y * ry, s, s);
  }
  ctx.globalAlpha = 1;
}

export async function generateScoreCardBlob({ score, verdict, regions, idea }) {
  await Promise.all([
    document.fonts.load("100px 'Bebas Neue'"),
    document.fonts.load("italic 44px 'Cormorant Garamond'"),
    document.fonts.load("500 30px 'Outfit'"),
  ]).catch(() => {});

  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = JET;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(200,162,76,0.55)";
  ctx.lineWidth = 2;
  ctx.strokeRect(42, 42, W - 84, H - 84);

  ctx.textAlign = "center";
  ctx.letterSpacing = "8px";
  ctx.font = "500 26px 'Outfit'";
  ctx.fillStyle = "rgba(243,240,233,0.6)";
  ctx.fillText("M MEDIA CORP — NEURO-EDITORIAL INSTRUMENT", W / 2, 150);

  drawBrainDots(ctx, W / 2, 400, 260, 200);

  ctx.font = "500 30px 'Outfit'";
  ctx.fillStyle = GOLD;
  ctx.fillText("MY M SCORE™", W / 2, 700);

  ctx.letterSpacing = "0px";
  ctx.font = "330px 'Bebas Neue'";
  ctx.fillStyle = GOLD;
  ctx.fillText(String(score), W / 2 - 40, 990);
  ctx.font = "70px 'Bebas Neue'";
  ctx.fillStyle = "rgba(243,240,233,0.45)";
  const scoreW = ctx.measureText(String(score)).width;
  ctx.textAlign = "left";
  ctx.font = "330px 'Bebas Neue'";
  const bigW = ctx.measureText(String(score)).width;
  ctx.font = "70px 'Bebas Neue'";
  ctx.fillText("/100", W / 2 - 40 + bigW / 2 + 18, 985);
  ctx.textAlign = "center";

  if (idea) {
    ctx.letterSpacing = "1px";
    ctx.font = "300 30px 'Outfit'";
    ctx.fillStyle = "rgba(243,240,233,0.5)";
    const ideaLines = wrapText(ctx, `“${idea}”`, 820, 2);
    ideaLines.forEach((l, i) => ctx.fillText(l, W / 2, 1090 + i * 42));
  }

  ctx.font = "italic 46px 'Cormorant Garamond'";
  ctx.fillStyle = CREAM;
  const vLines = wrapText(ctx, verdict, 860, 4);
  vLines.forEach((l, i) => ctx.fillText(l, W / 2, 1230 + i * 60));

  const top = (regions || []).slice(0, 3);
  const startY = 1530;
  top.forEach((r, i) => {
    const y = startY + i * 92;
    ctx.textAlign = "left";
    ctx.font = "38px 'Bebas Neue'";
    ctx.fillStyle = CREAM;
    ctx.fillText(r.name.toUpperCase(), 120, y);
    ctx.fillStyle = "rgba(243,240,233,0.12)";
    ctx.fillRect(120, y + 18, 700, 3);
    ctx.fillStyle = GOLD;
    ctx.fillRect(120, y + 18, 700 * (r.activation / 100), 3);
    ctx.textAlign = "right";
    ctx.font = "42px 'Bebas Neue'";
    ctx.fillStyle = GOLD;
    ctx.fillText(`${r.activation}%`, W - 120, y);
  });

  ctx.textAlign = "center";
  ctx.strokeStyle = "rgba(243,240,233,0.15)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(120, 1808);
  ctx.lineTo(W - 120, 1808);
  ctx.stroke();
  ctx.letterSpacing = "6px";
  ctx.font = "500 28px 'Outfit'";
  ctx.fillStyle = GOLD;
  ctx.fillText("TEST YOUR IDEA → MMEDIACORP.COM/MATTER", W / 2, 1862);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export async function shareScoreCard(data) {
  const blob = await generateScoreCardBlob(data);
  const file = new File([blob], `m-score-${data.score}.png`, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `My M Score: ${data.score}/100` });
      return "shared";
    } catch (e) {
      if (e.name === "AbortError") return "cancelled";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `m-score-${data.score}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return "downloaded";
}
